import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Mail, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { PromoBanner } from './PromoBanner';
import { useSiteContent } from '../hooks/useSiteContent';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/real-estate', label: 'Real Estate' },
  { href: '/editorial', label: 'Editorial' },
  { href: '/portfolio', label: 'Portfolio' },
  { href: '/blog', label: 'Blog' },
  { href: '/fine-art-prints', label: 'Fine Art Prints' },
  { href: '/about', label: 'About' },
];

export const Navigation = () => {
  const c = useSiteContent('header');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-nav shadow-sm' : 'bg-transparent'
      }`}
    >
      {/* Promo Banner */}
      <PromoBanner />

      {/* Top bar with contact info */}
      <div className="hidden lg:block bg-charcoal text-cream py-2">
        <div className="container-custom flex justify-end items-center gap-6 text-xs">
          <a
            href="mailto:hello@balduzziliving.com"
            className="flex items-center gap-2 hover:text-sand transition-colors"
            data-testid="header-email-link"
          >
            <Mail className="w-3 h-3" />
            hello@balduzziliving.com
          </a>
          <span className="flex items-center gap-2">
            <MapPin className="w-3 h-3" />
            {c('location', 'Byron Bay, NSW')}
          </span>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="container-custom py-4 flex items-center justify-between">
        {/* Logo */}
        <Link
          to="/"
          data-testid="nav-logo"
        >
          <img src="/logo.png" alt="Balduzzi Living" width={168} height={45} loading="eager" className="h-10 md:h-11 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              className={`nav-link transition-colors duration-300 ${
                location.pathname === link.href
                  ? 'text-olive'
                  : 'text-charcoal hover:text-olive'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link to="/about#contact">
            <Button
              className="btn-primary text-xs tracking-wider"
              data-testid="nav-contact-btn"
            >
              Get in Touch
            </Button>
          </Link>
        </div>

        {/* Mobile Navigation */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Open menu"
              className="text-charcoal"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-6 h-6" />
            </Button>
          </SheetTrigger>
          <SheetContent
            side="right"
            className="bg-cream border-l border-charcoal/10 w-full sm:w-80"
          >
            <div className="flex flex-col h-full pt-8">
              <Link
                to="/"
                className="font-serif text-2xl font-medium text-charcoal mb-8"
                onClick={() => setIsOpen(false)}
              >
                Balduzzi <span className="font-semibold italic">Living</span>
              </Link>
              
              <nav className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                    className={`text-lg font-sans transition-colors duration-300 py-2 border-b border-charcoal/10 ${
                      location.pathname === link.href
                        ? 'text-olive'
                        : 'text-charcoal hover:text-olive'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
              </nav>

              <div className="mt-auto pb-8">
                <Link
                  to="/about#contact"
                  onClick={() => setIsOpen(false)}
                >
                  <Button
                    className="btn-primary w-full"
                    data-testid="mobile-contact-btn"
                  >
                    Get in Touch
                  </Button>
                </Link>
                
                <div className="mt-6 text-sm text-charcoal/70">
                  <a
                    href="mailto:hello@balduzziliving.com"
                    className="flex items-center gap-2 mb-2 hover:text-olive transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    hello@balduzziliving.com
                  </a>
                  <p className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Byron Bay, NSW
                  </p>
                </div>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </nav>
    </header>
  );
};
